import { useCallback, useEffect, useState } from 'react'

export type Platform = 'android' | 'ios' | 'desktop' | 'unsupported'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * `beforeinstallprompt` dispara UMA vez e cedo — antes de qualquer
 * componente montar. Quem não guarda o evento perde a chance e nunca
 * consegue instalar por botão. Daí a captura no módulo, no boot.
 * Ver docs/specs/11-pwa.md.
 */
let capturedPrompt: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()

export function capturePWAInstallPrompt(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('beforeinstallprompt', (event) => {
    // Impede o mini-infobar do Chrome: o convite é nosso, no momento certo.
    event.preventDefault()
    capturedPrompt = event as BeforeInstallPromptEvent
    for (const listener of listeners) listener()
  })

  window.addEventListener('appinstalled', () => {
    capturedPrompt = null
    for (const listener of listeners) listener()
  })
}

const DISMISS_KEY = 'steingym.instalar.dispensado'
const DISMISS_COUNT_KEY = 'steingym.instalar.dispensas'
const DISMISS_DAYS = 14
const PERMANENT_AFTER = 3

export interface PWAInstallState {
  canInstall: boolean
  isInstalled: boolean
  platform: Platform
  showIosInstructions: boolean
  /** true quando o aluno dispensou e o silêncio ainda vale. */
  dismissed: boolean
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  dismiss: () => void
}

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unsupported'

  const ua = navigator.userAgent
  // iPad se anuncia como Mac; maxTouchPoints desfaz o disfarce.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1)

  if (isIos) return 'ios'
  if (/Android/.test(ua)) return 'android'
  if (/Windows|Macintosh|Linux/.test(ua)) return 'desktop'

  return 'unsupported'
}

/** Em Chrome ou Firefox no iOS a instalação não funciona; instruir o passo
 *  errado é pior que não instruir. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return detectPlatform() === 'ios' && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS usa uma propriedade própria.
    (navigator as unknown as { standalone?: boolean }).standalone === true ||
    new URLSearchParams(window.location.search).get('source') === 'pwa'
  )
}

export function usePWAInstall(): PWAInstallState {
  const [available, setAvailable] = useState(() => capturedPrompt !== null)
  const [installed, setInstalled] = useState(isStandalone)
  const [dismissed, setDismissed] = useState(() => isDismissed())

  useEffect(() => {
    const listener = (): void => {
      setAvailable(capturedPrompt !== null)
      setInstalled(isStandalone())
    }

    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const platform = detectPlatform()

  const promptInstall = useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    if (!capturedPrompt) return 'unavailable'

    await capturedPrompt.prompt()
    const { outcome } = await capturedPrompt.userChoice

    if (outcome === 'accepted') {
      capturedPrompt = null
      setAvailable(false)
      setInstalled(true)
    }

    return outcome
  }, [])

  const dismiss = useCallback(() => {
    try {
      const count = Number(localStorage.getItem(DISMISS_COUNT_KEY) ?? '0') + 1
      localStorage.setItem(DISMISS_COUNT_KEY, String(count))

      // Terceira dispensa silencia de vez; segue disponível no Perfil.
      const until =
        count >= PERMANENT_AFTER
          ? Number.MAX_SAFE_INTEGER
          : Date.now() + DISMISS_DAYS * 86_400_000

      localStorage.setItem(DISMISS_KEY, String(until))
    } catch {
      // sem persistência; o convite reaparece na próxima sessão
    }

    setDismissed(true)
  }, [])

  return {
    canInstall: available && !installed,
    isInstalled: installed,
    platform,
    showIosInstructions: platform === 'ios' && !installed && isIosSafari(),
    dismissed,
    promptInstall,
    dismiss,
  }
}

function isDismissed(): boolean {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) ?? '0')
    return Date.now() < until
  } catch {
    return false
  }
}

// ── Registro do worker ────────────────────────────────────────

export interface ServiceWorkerState {
  updateAvailable: boolean
  applyUpdate: () => void
}

let waitingWorker: ServiceWorker | null = null
const updateListeners = new Set<() => void>()

/**
 * Registra o worker em /app/:slug/sw.js com escopo /app/:slug/.
 *
 * Um worker em /sw.js com scope /app/:slug/ seria RECUSADO pelo navegador
 * sem o header Service-Worker-Allowed. Servir o mesmo arquivo em cada
 * caminho de slug resolve — e é o que dá a cada personal um PWA
 * independente. Ver docs/specs/11-pwa.md.
 */
export async function registerServiceWorker(slug: string): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  if (import.meta.env.DEV) return

  try {
    const registration = await navigator.serviceWorker.register(`/app/${slug}/sw.js`, {
      scope: `/app/${slug}/`,
      updateViaCache: 'none',
    })

    if (registration.waiting) notifyUpdate(registration.waiting)

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing
      if (!installing) return

      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          notifyUpdate(installing)
        }
      })
    })

    // Checa atualização na abertura e a cada hora com o app aberto.
    void registration.update()
    setInterval(() => void registration.update(), 60 * 60_000)
  } catch {
    // Registro falhou: o app funciona normalmente, sem offline.
  }
}

function notifyUpdate(worker: ServiceWorker): void {
  waitingWorker = worker
  for (const listener of updateListeners) listener()
}

export function useServiceWorkerUpdate(): ServiceWorkerState {
  const [updateAvailable, setUpdateAvailable] = useState(waitingWorker !== null)

  useEffect(() => {
    const listener = (): void => setUpdateAvailable(waitingWorker !== null)
    updateListeners.add(listener)
    return () => {
      updateListeners.delete(listener)
    }
  }, [])

  const applyUpdate = useCallback(() => {
    waitingWorker?.postMessage({ type: 'SKIP_WAITING' })
    // O controllerchange recarrega; um reload direto perderia a troca.
    navigator.serviceWorker?.addEventListener('controllerchange', () => {
      window.location.reload()
    })
  }, [])

  return { updateAvailable, applyUpdate }
}
